/* Banking Portal · Waves 25/26/31 (Feb 2026).
   Three operator-grade pages:
     Wave-25 · /banking/pre-auth-simulator    — dry-run any proposed txn
     Wave-26 · /banking/watchtower             — live money-stream feed + freeze button
     Wave-31 · /banking/network-risk           — 2-hop counterparty graph
*/
import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Beaker, Activity, Network, Play, Pause, Snowflake, AlertTriangle,
  CheckCircle2, XCircle, RefreshCw, Search, ChevronRight, ShieldAlert,
  Eye, Zap, Hash, Clock, Filter,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";

function fmt(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US").format(n);
}
function money(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}
function ts(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString(); } catch { return iso; }
}

const TONE_CLS = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber:   "bg-amber-50 text-amber-700 border-amber-200",
  rose:    "bg-rose-50 text-rose-700 border-rose-200",
  sky:     "bg-sky-50 text-sky-700 border-sky-200",
  slate:   "bg-slate-50 text-slate-700 border-slate-200",
};

const STATUS_CLS = {
  passed:           "bg-emerald-50 text-emerald-700 border-emerald-200",
  blocked:          "bg-rose-50 text-rose-700 border-rose-200",
  exceeded:         "bg-rose-50 text-rose-700 border-rose-200",
  warning:          "bg-amber-50 text-amber-700 border-amber-200",
  override_applied: "bg-violet-50 text-violet-700 border-violet-200",
  informational:    "bg-slate-50 text-slate-600 border-slate-200",
};

function Kpi({ label, value, sub, tone = "slate", icon: Icon, testid }) {
  const cls = {
    slate:   "bg-slate-50 border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
    sky:     "bg-sky-50 border-sky-200",
  }[tone] || "bg-slate-50 border-slate-200";
  return (
    <div data-testid={testid} className={`rounded-xl border ${cls} px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-25 · PRE-AUTHORIZATION TRANSACTION SIMULATOR
// ═════════════════════════════════════════════════════════════════════════
export function BankingPreAuthSimulator() {
  const [amount, setAmount] = useState("");
  const [payerId, setPayerId] = useState("");
  const [payeeId, setPayeeId] = useState("");
  const [kind, setKind] = useState("transfer");
  const [hour, setHour] = useState("");
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [recent, setRecent] = useState([]);

  const loadRecent = () =>
    api.get("/partner/banking/pre-auth/recent?limit=20")
      .then(r => setRecent(r.data.rows || []))
      .catch(() => {});

  useEffect(() => { loadRecent(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!amount || !payerId) {
      toast.error("Amount + payer ID required");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const body = {
        amount:          parseFloat(amount),
        payer_id:        payerId.trim(),
        payee_id:        payeeId.trim() || undefined,
        kind,
        hour_of_day_utc: hour === "" ? undefined : parseInt(hour, 10),
        notes:           notes || undefined,
      };
      const r = await api.post("/partner/banking/pre-auth/simulate", body);
      setResult(r.data);
      loadRecent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Simulation failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="banking-pre-auth">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Beaker className="w-6 h-6 text-violet-500" />
            Pre-Authorization Simulator
          </h1>
          <Badge className="bg-violet-50 text-violet-700 border-violet-200">Wave 25</Badge>
          <Badge variant="outline" className="text-slate-500">No money moves</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Dry-run any proposed transaction through all <strong>16 AML typologies</strong> + the <strong>8-step policy gate</strong> before it would ever settle.
          Examiner-grade demonstration of "we acted before it happened".
        </p>
      </div>

      <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="pre-auth-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11.5px]">Amount (USD)</Label>
            <Input type="number" min="1" max="10000000" step="100" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 9500" data-testid="input-amount" />
          </div>
          <div>
            <Label className="text-[11.5px]">Payer user ID</Label>
            <Input value={payerId} onChange={e => setPayerId(e.target.value)} placeholder="uuid" data-testid="input-payer" />
          </div>
          <div>
            <Label className="text-[11.5px]">Payee user ID (optional)</Label>
            <Input value={payeeId} onChange={e => setPayeeId(e.target.value)} placeholder="uuid" data-testid="input-payee" />
          </div>
          <div>
            <Label className="text-[11.5px]">Kind</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="select-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="transfer">Transfer</SelectItem>
                <SelectItem value="wallet_topup">Wallet top-up</SelectItem>
                <SelectItem value="invoice_payment">Invoice payment</SelectItem>
                <SelectItem value="withdrawal">Withdrawal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11.5px]">Hour of day (UTC, optional)</Label>
            <Input type="number" min="0" max="23" value={hour} onChange={e => setHour(e.target.value)} placeholder="0-23 · default = now" data-testid="input-hour" />
          </div>
          <div>
            <Label className="text-[11.5px]">Notes (optional)</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Why are you running this?" data-testid="input-notes" />
          </div>
        </div>
        <div>
          <Button type="submit" disabled={running} data-testid="btn-simulate" className="bg-violet-600 hover:bg-violet-700 text-white">
            {running ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Run dry-run
          </Button>
        </div>
      </form>

      {result && (
        <div className="space-y-4" data-testid="simulation-result">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <Badge className={TONE_CLS[result.summary.tone]}>
                  {result.summary.decision === "approved" && <CheckCircle2 className="w-3.5 h-3.5 mr-1 inline" />}
                  {result.summary.decision === "would_block" && <XCircle className="w-3.5 h-3.5 mr-1 inline" />}
                  {result.summary.decision === "approved_with_warnings" && <AlertTriangle className="w-3.5 h-3.5 mr-1 inline" />}
                  {result.summary.decision.replaceAll("_", " ").toUpperCase()}
                </Badge>
                <div className="text-2xl font-semibold tabular-nums text-slate-900">{money(result.request.amount)}</div>
                <div className="text-sm text-slate-500">{result.request.kind}</div>
              </div>
              <div className="text-[10.5px] text-slate-500">Simulation ID: {result.simulation_id?.slice(0, 8)}…</div>
            </div>

            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[9.5px] uppercase tracking-wider text-slate-500">Blocked</div>
                <div className="text-lg tabular-nums">{result.summary.blocked_count}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[9.5px] uppercase tracking-wider text-slate-500">Warnings</div>
                <div className="text-lg tabular-nums">{result.summary.warning_count}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[9.5px] uppercase tracking-wider text-slate-500">AML critical fired</div>
                <div className="text-lg tabular-nums">{result.summary.rules_fired_critical}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[9.5px] uppercase tracking-wider text-slate-500">AML high fired</div>
                <div className="text-lg tabular-nums">{result.summary.rules_fired_high}</div>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 text-[11.5px]">
              <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Payer</div>
                <div className="font-medium text-slate-800 mt-0.5">{result.parties.payer.name_masked}</div>
                <div className="text-slate-500 text-[10.5px]">{result.parties.payer.email_masked} · {result.parties.payer.company} · {result.parties.payer.role}</div>
                <div className="mt-1 flex gap-1 flex-wrap">
                  <Badge className={result.parties.payer.is_suspended ? TONE_CLS.rose : TONE_CLS.emerald}>{result.parties.payer.is_suspended ? "suspended" : "active"}</Badge>
                  <Badge className={result.parties.payer.kyc_status === "approved" ? TONE_CLS.emerald : TONE_CLS.amber}>KYC: {result.parties.payer.kyc_status}</Badge>
                </div>
              </div>
              {result.parties.payee.found && (
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Payee</div>
                  <div className="font-medium text-slate-800 mt-0.5">{result.parties.payee.name_masked}</div>
                  <div className="text-slate-500 text-[10.5px]">{result.parties.payee.email_masked} · {result.parties.payee.company} · {result.parties.payee.role}</div>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" />8-Step Policy Gate</div>
            <div className="space-y-1.5">
              {result.policy_gate.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-[11.5px] border-b border-slate-100 last:border-0 py-1.5" data-testid={`gate-step-${s.step}`}>
                  <div className="flex items-center gap-2">
                    <Hash className="w-3 h-3 text-slate-400" />
                    <span className="font-mono">{i + 1}. {s.step}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 text-[10.5px]">{s.detail}</span>
                    <Badge className={STATUS_CLS[s.status] || STATUS_CLS.informational}>{s.status.replaceAll("_", " ")}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-rose-500" />AML Typology Coverage</div>
            <div className="space-y-1.5">
              {result.aml_rules.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-[11.5px] border-b border-slate-100 last:border-0 py-1.5" data-testid={`aml-rule-${r.rule}`}>
                  <div className="flex items-center gap-2">
                    {r.would_fire ? <AlertTriangle className="w-3.5 h-3.5 text-rose-500" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                    <span className="font-mono">{r.rule}</span>
                    <Badge className={TONE_CLS[r.severity === "critical" || r.severity === "high" ? "rose" : "amber"]}>{r.severity}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 text-[10.5px] text-right max-w-[60%]">{r.detail}</span>
                    <Badge className={r.would_fire ? TONE_CLS.rose : TONE_CLS.emerald}>{r.would_fire ? "WOULD FIRE" : "no fire"}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-slate-500 italic">{result.attestation}</div>
        </div>
      )}

      {!!recent.length && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><Clock className="w-4 h-4 text-slate-500" />Recent simulations</div>
          <div className="space-y-1 text-[11px]">
            {recent.slice(0, 10).map(r => (
              <div key={r.id} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-1">
                <div className="font-mono text-slate-600">{ts(r.simulated_at)}</div>
                <div className="tabular-nums">{money(r.amount)}</div>
                <Badge className={TONE_CLS[r.tone]}>{r.decision}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// Wave-26 · LIVE MONEY-STREAM WATCHTOWER
// ═════════════════════════════════════════════════════════════════════════
export function BankingWatchtower() {
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [paused, setPaused] = useState(false);
  const [autoPaused, setAutoPaused] = useState(false);
  const [freezeOpen, setFreezeOpen] = useState(false);
  const [freezeTarget, setFreezeTarget] = useState(null);
  const [freezeReason, setFreezeReason] = useState("");
  const [freezing, setFreezing] = useState(false);
  const pollRef = useRef(null);
  const pausedRef = useRef(false);
  pausedRef.current = paused;

  const fetchOnce = async () => {
    if (pausedRef.current) return;
    try {
      const q = cursor ? `?since=${encodeURIComponent(cursor)}` : "";
      const r = await api.get(`/partner/banking/watchtower/feed${q}`);
      const incoming = r.data.rows || [];
      if (incoming.length) {
        setRows(prev => [...incoming, ...prev].slice(0, 300));
        setCursor(r.data.cursor);
      } else if (!cursor) {
        // First load with no rows — set cursor anyway
        setCursor(r.data.cursor);
      }
      if (r.data.auto_pause) {
        setAutoPaused(true);
        setPaused(true);
      }
    } catch {
      // silent fail; will retry on next tick
    }
  };

  useEffect(() => {
    fetchOnce();
    pollRef.current = setInterval(fetchOnce, 4000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitFreeze = async () => {
    if (!freezeTarget || freezeReason.length < 8) {
      toast.error("Reason ≥8 chars required");
      return;
    }
    setFreezing(true);
    try {
      await api.post("/partner/banking/watchtower/freeze", {
        user_id: freezeTarget.user_id,
        reason:  freezeReason,
        confirm: true,
      });
      toast.success("Customer frozen · audit chain stamped");
      setFreezeOpen(false);
      setFreezeReason("");
      setFreezeTarget(null);
      // Mark suspended in current view
      setRows(prev => prev.map(r => r.user_id === freezeTarget.user_id ? { ...r, user_suspended: true } : r));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Freeze failed");
    } finally {
      setFreezing(false);
    }
  };

  const totalUsd = rows.reduce((sum, r) => sum + Math.abs(r.amount || 0), 0);
  const highRisk = rows.filter(r => r.tone === "rose").length;
  const warnings = rows.filter(r => r.tone === "amber").length;

  return (
    <div className="space-y-5" data-testid="banking-watchtower">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-emerald-500" />
            Live Money-Stream Watchtower
          </h1>
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Wave 26</Badge>
          <Badge className={paused ? TONE_CLS.amber : TONE_CLS.emerald}>
            {paused ? <Pause className="w-3 h-3 mr-1 inline" /> : <Play className="w-3 h-3 mr-1 inline" />}
            {paused ? (autoPaused ? "Auto-paused on rule fire" : "Paused") : "Live · polling every 4s"}
          </Badge>
          <Button size="sm" variant="outline" onClick={() => { setPaused(p => !p); setAutoPaused(false); }} data-testid="btn-toggle-pause">
            {paused ? "Resume" : "Pause"}
          </Button>
          <Button size="sm" variant="outline" onClick={fetchOnce} data-testid="btn-refresh-feed">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
          </Button>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Real-time supervision feed of every <code>wallet_txn</code> as it lands. Auto-pauses when any txn scores rose-tier risk.
          One-click customer freeze with dual-control + audit chain.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Txns in window"  value={fmt(rows.length)} sub="Newest first" icon={Activity} testid="kpi-feed-count" />
        <Kpi label="Volume in window" value={money(totalUsd)} sub="Absolute USD" icon={Hash} testid="kpi-feed-volume" />
        <Kpi label="High-risk"        value={fmt(highRisk)} sub="Rose-tier rows" tone="rose"  icon={AlertTriangle} testid="kpi-feed-high" />
        <Kpi label="Warnings"         value={fmt(warnings)} sub="Amber-tier rows" tone="amber" icon={Eye} testid="kpi-feed-warn" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-[11.5px]">
          <thead className="bg-slate-50 sticky top-0">
            <tr className="text-left text-slate-500">
              <th className="px-3 py-2">Time</th>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Customer (masked)</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2">Risk</th>
              <th className="px-3 py-2">Flags</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.txn_id || i} className={`border-t border-slate-100 ${r.tone === "rose" ? "bg-rose-50/40" : r.tone === "amber" ? "bg-amber-50/30" : ""}`} data-testid={`feed-row-${i}`}>
                <td className="px-3 py-2 font-mono text-[10.5px] text-slate-600">{ts(r.created_at)}</td>
                <td className="px-3 py-2">{r.kind || "—"}</td>
                <td className="px-3 py-2">
                  <div>{r.user_name_masked}</div>
                  <div className="text-[10px] text-slate-400">{r.user_email_masked} · {r.user_company}</div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{money(r.amount)}</td>
                <td className="px-3 py-2"><Badge className={TONE_CLS[r.tone]}>{r.risk_score}</Badge></td>
                <td className="px-3 py-2 text-[10.5px] text-slate-500">{(r.flags || []).join(" · ") || "—"}</td>
                <td className="px-3 py-2 text-right">
                  {r.user_id && !r.user_suspended ? (
                    <Button size="sm" variant="outline" onClick={() => { setFreezeTarget(r); setFreezeOpen(true); }} data-testid={`btn-freeze-${i}`} className="text-rose-600 border-rose-200 hover:bg-rose-50">
                      <Snowflake className="w-3 h-3 mr-1" />Freeze
                    </Button>
                  ) : r.user_suspended ? (
                    <Badge className={TONE_CLS.rose}>Frozen</Badge>
                  ) : "—"}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Waiting for activity…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={freezeOpen} onOpenChange={setFreezeOpen}>
        <DialogContent data-testid="dialog-freeze">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Snowflake className="w-5 h-5 text-rose-500" />Freeze customer · dual-control</DialogTitle>
            <DialogDescription>
              This will immediately suspend <strong>{freezeTarget?.user_name_masked}</strong> ({freezeTarget?.user_company}) and chain-log the action.
              Use only when fraud or active abuse is suspected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-[11.5px]">Reason (≥8 chars · audit-logged)</Label>
            <Input value={freezeReason} onChange={e => setFreezeReason(e.target.value)} placeholder="e.g. Structuring pattern · 3x $9.5K in 24h" data-testid="input-freeze-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFreezeOpen(false)}>Cancel</Button>
            <Button onClick={submitFreeze} disabled={freezing || freezeReason.length < 8} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="btn-confirm-freeze">
              {freezing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Snowflake className="w-4 h-4 mr-2" />}
              Freeze customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// Wave-31 · NETWORK RISK GRAPH
// ═════════════════════════════════════════════════════════════════════════
export function BankingNetworkRisk() {
  const [search, setSearch] = useState("");
  const [graph, setGraph] = useState(null);
  const [paths, setPaths] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/partner/banking/network-risk/high-risk-paths?days=90&limit=20")
      .then(r => setPaths(r.data))
      .catch(() => toast.error("High-risk paths failed"));
  }, []);

  const loadGraph = async () => {
    if (!search.trim()) return;
    setLoading(true);
    setGraph(null);
    try {
      const r = await api.get(`/partner/banking/network-risk/customer/${encodeURIComponent(search.trim())}`);
      setGraph(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Graph not found");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="banking-network-risk">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Network className="w-6 h-6 text-rose-500" />
            Network Risk Graph
          </h1>
          <Badge className="bg-rose-50 text-rose-700 border-rose-200">Wave 31</Badge>
          <Badge variant="outline" className="text-slate-500">FinCEN follow-the-money</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Visual 2-hop counterparty graph. Surfaces any OFAC-hit or T3-risk entity within two hops of any of our customers.
        </p>
      </div>

      {paths && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4" data-testid="high-risk-paths">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="font-semibold text-rose-900 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" />High-risk paths in the last 90 days
            </div>
            <Badge className={TONE_CLS.rose}>{paths.count} path{paths.count === 1 ? "" : "s"}</Badge>
          </div>
          {paths.count === 0 ? (
            <div className="text-[12px] text-emerald-900 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />No high-risk entity within 2 hops of any active customer. ✓
            </div>
          ) : (
            <div className="space-y-1.5">
              {paths.rows.map((p, i) => (
                <div key={i} className="flex items-center justify-between bg-white rounded-lg border border-rose-200 px-3 py-2 text-[11.5px]" data-testid={`risk-path-${i}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge className={TONE_CLS[p.our_customer.tone]}>{p.our_customer.tier}</Badge>
                    <span className="font-medium truncate">{p.our_customer.name_masked} · {p.our_customer.company}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                    <Badge className={TONE_CLS[p.risky_entity.tone]}>{p.risky_entity.tier}</Badge>
                    <span className="font-medium truncate">{p.risky_entity.name_masked} · {p.risky_entity.company}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[10.5px]">
                    <span className="text-slate-500">{p.path_hops} hop</span>
                    <span className="tabular-nums font-medium text-slate-700">{money(p.edge_usd)}</span>
                    <span className="text-slate-400">{p.txns} txns</span>
                    <Button size="sm" variant="outline" onClick={() => { setSearch(p.our_customer.id); setTimeout(loadGraph, 50); }} data-testid={`btn-trace-${i}`}>
                      Trace
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <Label className="text-[11.5px]">Customer user-ID — view 2-hop counterparty network</Label>
        <div className="flex items-center gap-2 mt-1">
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="uuid" data-testid="input-graph-search" />
          <Button onClick={loadGraph} disabled={loading || !search.trim()} data-testid="btn-load-graph">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {graph && <GraphVisualization graph={graph} />}
    </div>
  );
}


function GraphVisualization({ graph }) {
  // Lay out nodes in concentric rings. Pure SVG, no external libs.
  const W = 760, H = 480;
  const CX = W / 2, CY = H / 2;
  const RING_R = [0, 130, 220];
  const layout = {};
  const byDepth = { 0: [], 1: [], 2: [] };
  graph.nodes.forEach(n => byDepth[n.depth].push(n));
  Object.keys(byDepth).forEach(d => {
    const list = byDepth[d];
    const r = RING_R[d];
    const n = Math.max(list.length, 1);
    list.forEach((node, i) => {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      layout[node.id] = {
        x: CX + r * Math.cos(angle),
        y: CY + r * Math.sin(angle),
        node,
      };
    });
  });

  const nodeFill = (n) => ({
    rose:    "#fee2e2",
    amber:   "#fef3c7",
    emerald: "#d1fae5",
    slate:   "#f1f5f9",
  }[n.tone] || "#f1f5f9");
  const nodeStroke = (n) => ({
    rose:    "#ef4444",
    amber:   "#f59e0b",
    emerald: "#10b981",
    slate:   "#94a3b8",
  }[n.tone] || "#94a3b8");

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="graph-svg-container">
      <div className="flex items-center justify-between mb-2 text-[11.5px]">
        <div className="flex items-center gap-3 flex-wrap">
          <Badge className={TONE_CLS.slate}>{graph.node_count} nodes</Badge>
          <Badge className={TONE_CLS.slate}>{graph.edge_count} edges</Badge>
          <Badge className={TONE_CLS.rose}>{graph.high_risk_in_graph_count} high-risk in graph</Badge>
          <Badge variant="outline">window: {graph.window_days}d</Badge>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 480 }} data-testid="graph-svg">
        {/* Edges */}
        {graph.edges.map((e, i) => {
          const a = layout[e.source], b = layout[e.target];
          if (!a || !b) return null;
          const w = Math.min(8, 1 + Math.log10((e.weight_usd || 1) + 1));
          return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#cbd5e1" strokeWidth={w} opacity="0.6" />;
        })}
        {/* Nodes */}
        {graph.nodes.map((n, i) => {
          const p = layout[n.id];
          if (!p) return null;
          const r = n.depth === 0 ? 26 : n.depth === 1 ? 16 : 12;
          return (
            <g key={i} transform={`translate(${p.x}, ${p.y})`}>
              <circle r={r} fill={nodeFill(n)} stroke={nodeStroke(n)} strokeWidth="2" />
              <text textAnchor="middle" y={r + 14} fontSize="9" fill="#475569">
                {(n.name_masked || n.id).slice(0, 14)}
              </text>
              {n.sanctions_hit && <text textAnchor="middle" y="4" fontSize="12" fill="#dc2626" fontWeight="bold">!</text>}
            </g>
          );
        })}
      </svg>
      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
        {graph.nodes.filter(n => n.depth > 0).slice(0, 10).map((n, i) => (
          <div key={i} className="text-[11px] flex items-center justify-between rounded border border-slate-100 px-2 py-1.5">
            <div className="flex items-center gap-2 min-w-0">
              <Badge className={TONE_CLS[n.tone]}>{n.tier}</Badge>
              <span className="truncate">{n.name_masked} · {n.company}</span>
            </div>
            <span className="text-slate-400">depth {n.depth}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
